import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Switch,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";

export default function WaitlistClientsTable() {
  const toast = useToast();
  const defaultCopy = {};
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [copy, setCopy] = useState(defaultCopy);
  const [counts, setCounts] = useState({ all: 0, on: 0, off: 0 });
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [queryInput, setQueryInput] = useState("");
  const [query, setQuery] = useState("");
  const [waitlistFilter, setWaitlistFilter] = useState("all");
  const [updatingClientIds, setUpdatingClientIds] = useState(new Set());
  const [pendingOptInClient, setPendingOptInClient] = useState(null);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setPage(1);
      setQuery(queryInput.trim());
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [queryInput]);

  useEffect(() => {
    fetchClients(page);
  }, [page, query, waitlistFilter]);

  const fetchClients = async (nextPage = 1) => {
    setLoading(true);

    try {
      const params = new URLSearchParams({
        page: String(nextPage),
        waitlist: waitlistFilter,
      });
      if (query) params.set("query", query);

      const response = await fetch(`/api/waitlist_clients?${params.toString()}`);
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to load waitlist clients");
      }

      setClients(data.clients || []);
      setCopy(data.copy || defaultCopy);
      setCounts(data.counts || { all: 0, on: 0, off: 0 });
      setPagination(data.pagination || null);
    } catch (error) {
      setClients([]);
      setCopy(defaultCopy);
      setCounts({ all: 0, on: 0, off: 0 });
      setPagination(null);
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (clientId, nextValue) => {
    setUpdatingClientIds((prev) => new Set(prev).add(clientId));
    setClients((prev) =>
      prev.map((client) =>
        client.id === clientId ? { ...client, waitlist_opted_in: nextValue } : client
      )
    );

    try {
      const response = await fetch(`/api/waitlist_clients/${clientId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          client: { waitlist_opted_in: nextValue },
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to update waitlist status");
      }

      setClients((prev) =>
        prev.map((client) => (client.id === clientId ? data.client : client))
      );
      fetchClients(page);
    } catch (error) {
      setClients((prev) =>
        prev.map((client) =>
          client.id === clientId ? { ...client, waitlist_opted_in: !nextValue } : client
        )
      );
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setUpdatingClientIds((prev) => {
        const next = new Set(prev);
        next.delete(clientId);
        return next;
      });
    }
  };

  const handleSwitchChange = (client, nextValue) => {
    if (nextValue) {
      setPendingOptInClient(client);
      return;
    }

    handleToggle(client.id, false);
  };

  const emptyStateMessage = waitlistFilter === "on"
    ? "No clients are currently on the waitlist."
    : waitlistFilter === "off"
      ? "Everyone in this filtered list is currently on the waitlist."
      : "No clients match your search.";

  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={{ base: 4, md: 6 }} bg="white">
      <VStack align="stretch" spacing={4}>
        <Flex justify="space-between" align={{ base: "stretch", md: "center" }} gap={3} direction={{ base: "column", md: "row" }}>
          <Box>
            <Text fontSize="sm" color="gray.900" fontWeight="semibold">Waitlist Customers</Text>
            <Text fontSize="sm" color="gray.600">
              See who is currently on the waitlist, who is not, and update their status manually.
            </Text>
          </Box>
          <Input
            placeholder="Search by name, email, or phone"
            value={queryInput}
            onChange={(e) => setQueryInput(e.target.value)}
            maxW={{ base: "full", md: "320px" }}
          />
        </Flex>

        <HStack spacing={2} flexWrap="wrap">
          {[
            { value: "all", label: `All (${counts.all})` },
            { value: "on", label: `On Waitlist (${counts.on})` },
            { value: "off", label: `Not On Waitlist (${counts.off})` },
          ].map((option) => (
            <Button
              key={option.value}
              size="sm"
              variant={waitlistFilter === option.value ? "solid" : "outline"}
              colorScheme={waitlistFilter === option.value ? "blue" : "gray"}
              onClick={() => {
                setPage(1);
                setWaitlistFilter(option.value);
              }}
            >
              {option.label}
            </Button>
          ))}
        </HStack>

        <Grid templateColumns={{ base: "1fr", lg: "1fr" }} gap={2}>
          <GridItem display={{ base: "none", md: "grid" }} gridTemplateColumns="1.5fr 1.2fr 1.3fr 1.2fr 1fr" gap={3} px={3} py={2} borderBottom="1px solid" borderColor="gray.200">
            <Text fontSize="xs" color="gray.500">Client</Text>
            <Text fontSize="xs" color="gray.500">Phone</Text>
            <Text fontSize="xs" color="gray.500">Next Appointment</Text>
            <Text fontSize="xs" color="gray.500">Status</Text>
            <Text fontSize="xs" color="gray.500">Toggle</Text>
          </GridItem>

          {clients.map((client) => (
            <Grid
              key={client.id}
              templateColumns={{ base: "1fr", md: "1.5fr 1.2fr 1.3fr 1.2fr 1fr" }}
              gap={3}
              px={3}
              py={3}
              borderBottom="1px solid"
              borderColor="gray.100"
              alignItems="center"
              borderWidth={{ base: "1px", md: "0 0 1px 0" }}
              borderRadius={{ base: "md", md: 0 }}
              mb={{ base: 3, md: 0 }}
            >
              <Box>
                <Text display={{ base: "block", md: "none" }} fontSize="xs" color="gray.500" mb={1}>Client</Text>
                <Text fontSize="sm" color="gray.900" fontWeight="semibold">{client.full_name}</Text>
                <Text fontSize="xs" color="gray.500">{client.email || client.company_name || "No email on file"}</Text>
              </Box>
              <Box>
                <Text display={{ base: "block", md: "none" }} fontSize="xs" color="gray.500" mb={1}>Phone</Text>
                <Text fontSize="sm" color="gray.700">{client.phone_number || "No phone number"}</Text>
              </Box>
              <Box>
                <Text display={{ base: "block", md: "none" }} fontSize="xs" color="gray.500" mb={1}>Next Appointment</Text>
                <Text fontSize="sm" color="gray.700">{client.next_appointment_display || "No upcoming appointment"}</Text>
              </Box>
              <VStack align="start" spacing={1}>
                <Text display={{ base: "block", md: "none" }} fontSize="xs" color="gray.500">Status</Text>
                <Badge colorScheme={client.waitlist_opted_in ? "green" : "gray"} w="fit-content">
                  {client.waitlist_opted_in ? "On waitlist" : "Not on waitlist"}
                </Badge>
                {client.waitlist_opted_in && (
                  <Badge colorScheme={client.waitlist_double_opted_in ? "blue" : "orange"} variant="subtle" w="fit-content">
                    {client.waitlist_double_opted_in ? "Confirmed" : "Pending confirmation"}
                  </Badge>
                )}
              </VStack>
              <HStack spacing={2} minH="44px">
                <Text display={{ base: "block", md: "none" }} fontSize="xs" color="gray.500" mr={2}>Waitlist</Text>
                <Switch
                  aria-label={`Toggle waitlist for ${client.full_name}`}
                  isChecked={client.waitlist_opted_in}
                  onChange={(e) => handleSwitchChange(client, e.target.checked)}
                  isDisabled={updatingClientIds.has(client.id)}
                  colorScheme="green"
                />
                {updatingClientIds.has(client.id) && <Spinner size="xs" color="blue.500" />}
              </HStack>
            </Grid>
          ))}
        </Grid>

        {!loading && clients.length === 0 && (
          <Box py={6} textAlign="center">
            <Text fontSize="sm" color="gray.500">{emptyStateMessage}</Text>
          </Box>
        )}

        {loading && (
          <HStack justify="center" py={6}>
            <Spinner size="sm" color="blue.500" />
          </HStack>
        )}

        {pagination && pagination.total_pages > 1 && (
          <Flex justify="space-between" align="center" pt={2} gap={3} flexWrap="wrap">
            <Button size="sm" variant="outline" onClick={() => setPage((prev) => prev - 1)} isDisabled={page <= 1}>
              Previous
            </Button>
            <Text fontSize="sm" color="gray.600">
              Page {pagination.current_page} of {pagination.total_pages}
            </Text>
            <Button size="sm" variant="outline" onClick={() => setPage((prev) => prev + 1)} isDisabled={page >= pagination.total_pages}>
              Next
            </Button>
          </Flex>
        )}
      </VStack>

      <Modal isOpen={!!pendingOptInClient} onClose={() => setPendingOptInClient(null)} isCentered size="md">
        <ModalOverlay />
        <ModalContent mx={{ base: 4, md: 0 }} maxH="calc(100dvh - 32px)">
          <ModalHeader>{copy.opt_in_modal?.title || "Confirm"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.700">
              {copy.opt_in_modal?.body || "Please confirm this waitlist change."}
            </Text>
          </ModalBody>
          <ModalFooter gap={3} flexDirection={{ base: "column-reverse", sm: "row" }} sx={{ "& > button": { width: { base: "100%", sm: "auto" }, minHeight: "44px" } }}>
            <Button variant="outline" onClick={() => setPendingOptInClient(null)}>
              {copy.opt_in_modal?.cancel_button || "Cancel"}
            </Button>
            <Button
              colorScheme="green"
              onClick={() => {
                const client = pendingOptInClient;
                setPendingOptInClient(null);
                if (client) handleToggle(client.id, true);
              }}
            >
              {copy.opt_in_modal?.confirm_button || "Confirm"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
